import {
  ASTKindToNode,
  ASTNode,
  DocumentNode,
  FragmentDefinitionNode,
  OperationDefinitionNode,
  Visitor,
  getVisitFn,
  visit,
} from 'graphql';

/**
 * Visit an ast, also visiting any seen fragments.
 */
export function visitThroughFragments(
  document: DocumentNode,
  visitor: Visitor<ASTKindToNode>,
): DocumentNode | null {
  const operation = document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  );

  if (!operation) {
    return null;
  }

  const fragments: Record<string, FragmentDefinitionNode> = {};
  document.definitions.forEach((definition) => {
    if (definition.kind === 'FragmentDefinition') {
      fragments[definition.name.value] = definition;
    }
  });

  const fragmentVisitor: Visitor<ASTKindToNode, ASTNode> = {
    enter(node, ...rest) {
      // We still want to visit the child first
      const fn = getVisitFn(visitor, node.kind, false);
      const result = fn?.apply(visitor, [node, ...rest]);

      if (node.kind === 'FragmentSpread') {
        const fragmentNode = fragments[node.name.value];
        if (fragmentNode) {
          const newFragmentNode = visit(fragmentNode, fragmentVisitor);
          fragments[node.name.value] = newFragmentNode;
        }
      }

      return result;
    },
    leave(node, ...rest) {
      const fn = getVisitFn(visitor, node.kind, true);
      return fn?.apply(visitor, [node, ...rest]);
    },
  };

  const newOperation: OperationDefinitionNode = visit(
    operation,
    fragmentVisitor,
  );
  return {
    ...document,
    definitions: [newOperation, ...Object.values(fragments)],
  };
}
